import { Theme, alpha } from "@mui/material/styles";
import type { CSSObject } from "type/tss";
import * as env from "shared/env";
import { baseColors } from "theme";

export const formFieldStyles = (theme: Theme) =>
	({
		// Read by screen readers, not shown (the standard clip pattern).
		visuallyHidden: {
			position: "absolute",
			width: 1,
			height: 1,
			padding: 0,
			margin: -1,
			overflow: "hidden",
			clip: "rect(0 0 0 0)",
			whiteSpace: "nowrap",
			border: 0,
		},
		fieldLabel: {
			display: "block",
			marginBottom: 8,
			fontSize: "0.875rem",
			lineHeight: "20px",
			color: theme.palette.custom.textSecondary,
			fontWeight: 400,
			fontFamily: theme.typography.fontFamily,
		},
		fieldLabelAsterisk: {
			color: theme.palette.error.main,
		},
		textFieldRoot: {
			backgroundColor: "white",
			borderRadius: 4,
			"& .MuiOutlinedInput-notchedOutline": {
				borderColor: theme.palette.custom.border,
				borderWidth: 1,
			},
			"&:hover": { backgroundColor: baseColors.gray75 },
			"&:hover .MuiOutlinedInput-notchedOutline": {
				borderColor: alpha(theme.palette.secondary.main, 0.4),
			},
			"&.Mui-focused": { backgroundColor: baseColors.gray75 },
			"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
				borderColor: theme.palette.secondary.main,
				borderWidth: 2,
			},
		},
		textFieldRootMultiline: {
			backgroundColor: "white",
			borderRadius: 4,
			"& .MuiOutlinedInput-notchedOutline": {
				borderColor: theme.palette.custom.border,
				borderWidth: 1,
			},
			"&:hover": { backgroundColor: baseColors.gray75 },
			"&:hover .MuiOutlinedInput-notchedOutline": {
				borderColor: alpha(theme.palette.secondary.main, 0.4),
			},
			"&.Mui-focused": { backgroundColor: baseColors.gray75 },
			"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
				borderColor: theme.palette.secondary.main,
				borderWidth: 2,
			},
		},
		textFieldFocused: {},
		textFieldNotchedOutline: {},
		textFieldInput: {
			padding: "12px 16px",
			fontSize: "1rem",
			lineHeight: "24px",
			fontFamily: theme.typography.fontFamily,
			color: theme.palette.custom.textPrimary,
		},
		// FormField's start adornment is an InputAdornment, whose own 8px margin spaces the value, so
		// the input drops its left padding as MUI's outlined input does; otherwise the 16 above doubles
		// the gap (MAX-4403 #1395 QA). Mapped only in FormField: other users of these styles draw their
		// own adornment without that margin (the Display colour picker's swatch) and keep the 16.
		textFieldInputAdornedStart: {
			paddingLeft: 0,
		},
		helperText: {
			margin: "0.25rem 0.875rem 0",
			fontSize: "0.75rem",
			color: theme.palette.error.main,
			fontFamily: theme.typography.fontFamily,
		},
		// Guidance rather than an error, so the neutral text colour (MAX-4419 #1373).
		hint: {
			margin: "0.25rem 0.875rem 0",
			fontSize: "0.75rem",
			color: theme.palette.custom.textSecondary,
			fontFamily: theme.typography.fontFamily,
		},
		selectInputRoot: {
			backgroundColor: "white",
			borderRadius: 4,
			"& .MuiOutlinedInput-notchedOutline": {
				borderColor: theme.palette.custom.border,
				borderWidth: 1,
			},
			"&:hover": { backgroundColor: baseColors.gray75 },
			"&:hover .MuiOutlinedInput-notchedOutline": {
				borderColor: alpha(theme.palette.secondary.main, 0.4),
			},
			"&.Mui-focused": { backgroundColor: baseColors.gray75 },
			"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
				borderColor: theme.palette.secondary.main,
				borderWidth: 2,
			},
		},
		selectNotchedOutline: {},
		selectFocused: {},
		selectInput: {
			// Reserve room on the right for the chevron (16px icon + 16px inset + 8px gap)
			padding: "12px 40px 12px 16px",
			fontSize: "1rem",
			lineHeight: "24px",
			fontFamily: theme.typography.fontFamily,
			color: theme.palette.custom.textPrimary,
			"&:focus": {
				backgroundColor: "transparent",
			},
		},
		selectIcon: {
			right: 16,
			width: 16,
			height: 16,
			top: "calc(50% - 8px)",
			// Figma "Icon/field-inactive" (#e85f2a) — brand orange, not grey
			color: theme.palette.secondary.main,
			pointerEvents: "none",
		},
		endAdornment: {
			// Adornments (text like "%" and icons alike) use the field's active color —
			// Figma "Icon/field-inactive" / active border (#e85f2a) — overriding MUI's
			// default grey (text.secondary).
			color: theme.palette.secondary.main,
			"& .MuiTypography-root": {
				color: theme.palette.secondary.main,
			},
			"& svg": {
				color: theme.palette.secondary.main,
				display: "block",
			},
		},
	} satisfies Record<string, CSSObject>);
